
import { z } from 'zod';

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

export interface ResumeContentValidation {
  hasResumeContent: boolean;
  confidence: number;
  language: string;
  contentType: 'resume' | 'cv' | 'unknown';
}

export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
];

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export class ResumeValidator {
  static validateFileUpload(file: File): FileValidationResult {
    const warnings: string[] = [];
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, error: `File size (${Math.round(file.size / 1024 / 1024)}MB) exceeds limit of 50MB` };
    }

    // Check MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return { isValid: false, error: `File type '${file.type}' is not supported. Please upload PDF, DOC, DOCX, or TXT files.` };
    }

    // Check file extension
    const extension = file.name.toLowerCase().split('.').pop();
    const validExtensions = ['pdf', 'doc', 'docx', 'txt'];
    if (!extension || !validExtensions.includes(extension)) {
      warnings.push('File extension may not match content type');
    }

    // Check file name for suspicious patterns
    if (file.name.length > 255) {
      warnings.push('File name is unusually long');
    }

    return { isValid: true, warnings };
  }

  static detectResumeContent(text: string): ResumeContentValidation {
    const resumeKeywords = [
      'experience', 'education', 'skills', 'work', 'employment', 'career',
      'position', 'company', 'university', 'degree', 'certification',
      'responsibilities', 'achievements', 'projects', 'qualifications'
    ];

    const cvKeywords = [
      'curriculum vitae', 'cv', 'personal statement', 'publications',
      'research', 'academic', 'thesis', 'dissertation'
    ];

    const lowerText = text.toLowerCase();
    const resumeMatches = resumeKeywords.filter(keyword => lowerText.includes(keyword)).length;
    const cvMatches = cvKeywords.filter(keyword => lowerText.includes(keyword)).length;
    
    const totalWords = text.split(/\s+/).length;
    const confidence = Math.min((resumeMatches + cvMatches) / 10, 1);
    
    let contentType: 'resume' | 'cv' | 'unknown' = 'unknown';
    if (resumeMatches > 3 || cvMatches > 2) {
      contentType = cvMatches > resumeMatches ? 'cv' : 'resume';
    }

    // Basic language detection (simplified)
    const englishWords = ['the', 'and', 'is', 'to', 'of', 'in', 'for', 'with', 'on', 'at'];
    const englishMatches = englishWords.filter(word => lowerText.includes(word)).length;
    const language = englishMatches > 3 ? 'en' : 'unknown';

    return {
      hasResumeContent: confidence > 0.3,
      confidence,
      language,
      contentType
    };
  }
}

export const extractedDataSchema = z.object({
  skills: z.array(z.string()).optional(),
  experience_years: z.number().min(0).max(70).optional(),
  education: z.string().optional(),
  previous_roles: z.array(z.string()).optional(),
  certifications: z.array(z.string()).optional(),
  summary: z.string().optional(),
  contact_info: z.object({
    email: z.string().email().optional(),
    phone: z.string().optional(),
    linkedin: z.string().url().optional()
  }).optional()
});

export type ExtractedResumeData = z.infer<typeof extractedDataSchema>;
